// src/components/admin/act/report/ACTPerformanceReport.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

import "./ACTPerformanceReport.css";

// ---------------------- Charts ----------------------
function DonutChart({
  value,
  max = 100,
  size = 96,
  strokeWidth = 8,
  label,
  subLabel,
  accentColor = "#2563eb",
  trackColor = "#e5e7eb",
  valueFormatter,
  scoreFontSize = 14,
  ariaLabel,
}) {
  const safeMax = max || 1;
  const raw = value ?? 0;
  const ratio = Math.max(0, Math.min(raw / safeMax, 1));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - ratio);

  const displayValue =
    value == null
      ? "—"
      : valueFormatter
      ? valueFormatter(raw, safeMax)
      : `${Math.round(raw)}`;

  return (
    <div className="actpr-donutWrap">
      <svg
        width={size}
        height={size}
        className="actpr-donutSvg"
        role="img"
        aria-label={ariaLabel || (label ? `${label}: ${displayValue}` : `Donut: ${displayValue}`)}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={accentColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%"
          y="50%"
          dy=".1em"
          textAnchor="middle"
          dominantBaseline="middle"
          className="actpr-donutValue"
          style={{ fontSize: scoreFontSize }}
        >
          {displayValue}
        </text>
        <text
          x="50%"
          y="68%"
          textAnchor="middle"
          dominantBaseline="middle"
          className="actpr-donutSub"
        >
          {subLabel || ""}
        </text>
      </svg>

      {label && <div className="actpr-donutLabel">{label}</div>}
    </div>
  );
}

// ---------------------- Helpers ----------------------
const splitCorrect = (val) =>
  String(val || "")
    .split(/[,\s/|]+/)
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);

const getSubjectFromQid = (qid) => {
  const s = String(qid || "");
  const prefix = s.split("_")[0];
  return prefix || "Uncategorized";
};

const clampPct = (n) => Math.max(0, Math.min(n, 100));
const pct = (num, den) => (den > 0 ? clampPct((num / den) * 100) : 0);

function ScoreChip({ label, value }) {
  return (
    <span className="actpr-chip">
      <span className="actpr-chipLabel">{label}</span>
      <span className="actpr-chipValue">{value == null ? "—" : value}</span>
    </span>
  );
}

function StatPill({ tone, label, value }) {
  return (
    <span className={`actpr-pill actpr-pill--${tone || "neutral"}`}>
      <span className="actpr-pillLabel">{label}</span>
      <span className="actpr-pillValue">{value}</span>
    </span>
  );
}

function SubjectRow({ subjectKey, label, score, counts, hideDonutMax }) {
  const total = counts?.total || 0;
  const correct = counts?.correct || 0;
  const incorrect = counts?.incorrect || 0;
  const skipped = counts?.skipped || 0;

  const correctW = pct(correct, total);
  const incorrectW = pct(incorrect, total);
  const skippedW = Math.max(0, 100 - correctW - incorrectW);

  return (
    <div className="actpr-subjectRow">
      <div className="actpr-subjectMain">
        <div className="actpr-subjectTop">
          <div className="actpr-subjectName">{label}</div>
          {/* Optional: You can remove this text score if you want the donut to be the only source of truth. 
              I'm keeping it for now but you could conditionally hide it. */}
          <div className="actpr-subjectScore">
            <span className="actpr-scoreNum">{score == null ? "—" : score}</span>
            {!hideDonutMax && <span className="actpr-scoreMax">/36</span>}
          </div>
        </div>

        <div className="actpr-bar" aria-hidden="true">
          <span className="actpr-barSeg actpr-barSeg--correct" style={{ width: `${correctW}%` }} />
          <span className="actpr-barSeg actpr-barSeg--incorrect" style={{ width: `${incorrectW}%` }} />
          <span className="actpr-barSeg actpr-barSeg--skipped" style={{ width: `${skippedW}%` }} />
        </div>

        <div className="actpr-subjectMeta">
          <div className="actpr-pillRow">
            <StatPill tone="correct" label="Correct" value={correct} />
            <StatPill tone="incorrect" label="Incorrect" value={incorrect} />
            <StatPill tone="skipped" label="Skipped" value={skipped} />
            {/* Removed Total to save space and reduce clutter, as requested */}
          </div>
          
          {/* Note: Tiny text removed or kept minimal */}
        </div>
      </div>

      <div className="actpr-subjectDonut">
        <DonutChart
          value={score}
          max={36}
          size={80}        /* INCREASED from 72 */
          strokeWidth={6}   /* INCREASED from 6 */
          scoreFontSize={20} /* INCREASED from 16 */
          valueFormatter={(v) => (v == null ? "—" : `${v}`)}
          ariaLabel={`${label} score`}
        />
      </div>
    </div>
  );
}

export default function ACTPerformanceReport({ studentId, testId, summary }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId || !testId || !summary) {
      setReportData(null);
      setLoading(false);
      return;
    }

    const fetchReportData = async () => {
      setLoading(true);
      try {
        // 1) Fetch ACT question metadata
        const qSnapshot = await getDocs(collection(db, "act_diagnostic_tests", testId, "questions"));
        const questionDataMap = new Map();
        qSnapshot.forEach((d) => {
          const data = d.data() || {};
          const key = data.questionId || d.id;
          questionDataMap.set(key, data);
        });

        // 2) Answers from the attempt doc
        const studentAnswers = summary.answers || {};
        const scienceSkipped = !!summary.scienceSkipped;

        // Per-subject breakdown
        const subjectStats = {
          English: { total: 0, correct: 0, incorrect: 0, skipped: 0 },
          Math: { total: 0, correct: 0, incorrect: 0, skipped: 0 },
          Reading: { total: 0, correct: 0, incorrect: 0, skipped: 0 },
          Science: { total: 0, correct: 0, incorrect: 0, skipped: 0 },
        };

        const overall = { total: 0, correct: 0, incorrect: 0, skipped: 0 };

        for (const [qId, qDataRaw] of questionDataMap.entries()) {
          const qData = qDataRaw || {};
          const subject = qData.subject || getSubjectFromQid(qId);
          if (scienceSkipped && subject === "Science") continue;

          if (String(qData.category || "").toLowerCase() === "not scored") continue;

          const ansRaw = studentAnswers[qId] ?? "";
          const studentAns = String(ansRaw || "").trim().toUpperCase();
          const correctArr = splitCorrect(qData.correctAnswer);

          const isSkipped = !studentAns;
          const isCorrect = !isSkipped && correctArr.includes(studentAns);

          const bucket =
            subjectStats[subject] ||
            (subjectStats[subject] = { total: 0, correct: 0, incorrect: 0, skipped: 0 });

          bucket.total += 1;
          overall.total += 1;

          if (isSkipped) {
            bucket.skipped += 1;
            overall.skipped += 1;
          } else if (isCorrect) {
            bucket.correct += 1;
            overall.correct += 1;
          } else {
            bucket.incorrect += 1;
            overall.incorrect += 1;
          }
        }

        // Scores (prefer scaled)
        const scaleScores = summary.scaleScores || {};
        const rawScores = summary.rawScores || {};
        const getScore = (sub) => {
          const s = scaleScores?.[sub];
          if (typeof s === "number") return s;
          const r = rawScores?.[sub];
          if (typeof r === "number") return r;
          return null;
        };

        const math = getScore("Math");
        const sci = scienceSkipped ? null : getScore("Science");
        const eng = getScore("English");
        const read = getScore("Reading");

        const stem = scienceSkipped
          ? null
          : typeof math === "number" && typeof sci === "number"
          ? Math.round((math + sci) / 2)
          : summary.stemScore ?? null;

        const ela = typeof eng === "number" && typeof read === "number" ? Math.round((eng + read) / 2) : null;

        const composite =
          typeof summary.compositeScore === "number"
            ? summary.compositeScore
            : (() => {
                // fallback: compute from scaled scores if present
                const vals = [eng, math, read].filter((v) => typeof v === "number");
                if (!scienceSkipped && typeof sci === "number") vals.push(sci);
                if (vals.length === 0) return null;
                const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
                return Math.round(avg);
              })();

        const compositePct = composite == null ? null : Math.round((composite / 36) * 100);

        setReportData({
          overall,
          subjectStats,
          scores: {
            English: eng,
            Math: math,
            Reading: read,
            Science: sci,
            STEM: stem,
            ELA: ela,
            Composite: composite,
          },
          scienceSkipped,
          compositePct,
        });
      } catch (err) {
        console.error(err);
        setReportData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [studentId, testId, summary]);

  const ui = useMemo(() => {
    if (!reportData) return null;
    const { subjectStats, scores, overall, scienceSkipped, compositePct } = reportData;

    const totalPossible = 36;

    const group1Subjects = [
      { key: "Math", label: "Math", show: true },
      { key: "Science", label: "Science", show: !scienceSkipped },
    ];

    const group2Subjects = [
      { key: "English", label: "English", show: true },
      { key: "Reading", label: "Reading", show: true },
    ];

    const getCounts = (key) => {
      return subjectStats[key] || { total: 0, correct: 0, incorrect: 0, skipped: 0 };
    };

    return {
      scores,
      overall,
      scienceSkipped,
      compositePct,
      totalPossible,
      group1Subjects,
      group2Subjects,
      getCounts,
    };
  }, [reportData]);

  if (loading) return <div className="actpr-loading">Loading performance…</div>;
  if (!ui) return <div className="actpr-loading">No performance data.</div>;

  const {
    scores,
    overall,
    scienceSkipped,
    compositePct,
    totalPossible,
    group1Subjects,
    group2Subjects,
    getCounts,
  } = ui;

  const compositeLabel = scores.Composite == null ? "—" : `${scores.Composite} / ${totalPossible}`;

  return (
    <div className="actpr-root">
      {/* ---------------- OVERALL SUMMARY ---------------- */}
      <div className="actpr-sectionHead">
        <div>
          <div className="actpr-sectionTitle">Overall Summary</div>
        </div>
        {scienceSkipped && <span className="actpr-flag">Science skipped</span>}
      </div>

      <div className="actpr-summaryGrid">
        {/* Composite */}
        <div className="actpr-card actpr-card--emphasis">
          <div className="actpr-cardTop">
            <div>
              <div className="actpr-kicker">Composite Score</div>
              <div className="actpr-kickerSub">Rounded average of section scale scores</div>
            </div>
          </div>

          <div className="actpr-compositeBody">
            <div className="actpr-compositeDonut">
              <DonutChart
                value={scores.Composite}
                max={totalPossible}
                size={132}
                strokeWidth={12}
                scoreFontSize={30}
                valueFormatter={(v) => (v == null ? "—" : `${v}`)}
                ariaLabel={`Composite score ${compositeLabel}`}
              />
            </div>

            <div className="actpr-compositeMeta">
              <div className="actpr-bigScore">{compositeLabel}</div>

              <div className="actpr-chipRow">
                <ScoreChip label="English" value={scores.English} />
                <ScoreChip label="Math" value={scores.Math} />
                <ScoreChip label="Reading" value={scores.Reading} />
                {!scienceSkipped && <ScoreChip label="Science" value={scores.Science} />}
              </div>
            </div>
          </div>
        </div>

        {/* Accuracy */}
        <div className="actpr-card">
          <div className="actpr-cardTop">
            <div>
              <div className="actpr-kicker">Overall Accuracy</div>
              <div className="actpr-kickerSub">Composite shown as a percent of 36</div>
            </div>
          </div>

          <div className="actpr-accuracyBody">
            <div className="actpr-accuracyDonut">
              <DonutChart
                value={compositePct}
                max={100}
                size={100}
                strokeWidth={10}
                scoreFontSize={22}
                label="Your Score"
                subLabel="%"
                valueFormatter={(v) => (v == null ? "—" : `${Math.round(v)}%`)}
                ariaLabel="Composite percent"
              />
            </div>

            <div className="actpr-metricGrid">
              <div className="actpr-metric">
                <div className="actpr-metricLabel">Total</div>
                <div className="actpr-metricValue">{overall.total}</div>
              </div>
              <div className="actpr-metric">
                <div className="actpr-metricLabel">Correct</div>
                <div className="actpr-metricValue">{overall.correct}</div>
              </div>
              <div className="actpr-metric">
                <div className="actpr-metricLabel">Incorrect</div>
                <div className="actpr-metricValue">{overall.incorrect}</div>
              </div>
              <div className="actpr-metric">
                <div className="actpr-metricLabel">Skipped</div>
                <div className="actpr-metricValue">{overall.skipped}</div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ---------------- SUBJECT ANALYSIS ---------------- */}
      <div className="actpr-sectionHead" style={{ marginTop: 18 }}>
        <div>
          <div className="actpr-sectionTitle">Subject Analysis</div>
        </div>
      </div>

      <div className="actpr-groupGrid">
        <div className="actpr-groupCard">
          <div className="actpr-groupHead">
            {/* Changed Group 1 to STEM */}
            <div className="actpr-groupName">STEM</div>
            <div className="actpr-groupSub">Math, Science (optional)</div>
          </div>
          <div className="actpr-groupBody">
            {group1Subjects
              .filter((s) => s.show)
              .map((s) => (
                <SubjectRow
                  key={s.key}
                  subjectKey={s.key}
                  label={s.label}
                  score={scores[s.key]}
                  counts={getCounts(s.key)}
                />
              ))}
          </div>
        </div>

        <div className="actpr-groupCard">
          <div className="actpr-groupHead">
            {/* Changed Group 2 to ELA */}
            <div className="actpr-groupName">ELA</div>
            <div className="actpr-groupSub">English, Reading</div>
          </div>

          <div className="actpr-groupBody">
            {group2Subjects
              .filter((s) => s.show)
              .map((s) => (
                <SubjectRow
                  key={s.key}
                  subjectKey={s.key}
                  label={s.label}
                  score={scores[s.key]}
                  counts={getCounts(s.key)}
                />
              ))}
          </div>
        </div>
      </div>

      {scienceSkipped && <div className="actpr-footerNote">Science was skipped — Science and STEM are hidden.</div>}
    </div>
  );
}
